import { useState, useEffect } from 'react';
import { transactionsAPI, clientsAPI } from '../lib/api';
import { useCurrency } from '../context/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, TrendingUp, TrendingDown, DollarSign, Trash2, Loader2, Coins } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { format } from 'date-fns';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const INCOME_CATEGORIES = ['Service Fee', 'Consultation', 'Product Sale', 'Referral Bonus', 'Other'];
const EXPENSE_CATEGORIES = ['Supplies', 'Marketing', 'Software', 'Travel', 'Insurance', 'Office', 'Other'];

export default function Finances() {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const { currency, setCurrency, formatAmount, getCurrencySymbol, currencies } = useCurrency();
  const [formData, setFormData] = useState({
    type: 'income',
    amount: '',
    category: '',
    description: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    client_id: ''
  });

  const fetchData = async () => {
    try {
      const [transRes, summaryRes, clientsRes] = await Promise.all([
        transactionsAPI.getAll(),
        transactionsAPI.getSummary(),
        clientsAPI.getAll()
      ]);
      setTransactions(transRes.data);
      setSummary(summaryRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      type: 'income',
      amount: '',
      category: '',
      description: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      client_id: ''
    });
  };

  const openAddDialog = (type = 'income') => {
    resetForm();
    setFormData(prev => ({ ...prev, type }));
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }

    setSaving(true);
    try {
      await transactionsAPI.create({
        ...formData,
        amount: parseFloat(formData.amount)
      });
      toast.success(`${formData.type === 'income' ? 'Income' : 'Expense'} recorded`);
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save transaction');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (transaction) => {
    if (!window.confirm('Delete this transaction?')) return;
    
    try {
      await transactionsAPI.delete(transaction.id);
      toast.success('Transaction deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete transaction');
    }
  };

  const filteredTransactions = transactions.filter(t => {
    if (activeTab === 'income') return t.type === 'income';
    if (activeTab === 'expense') return t.type === 'expense';
    return true;
  });

  // Prepare chart data
  const chartData = summary?.monthly ? 
    Object.entries(summary.monthly)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([month, data]) => ({
        month: format(new Date(month + '-01'), 'MMM'),
        income: data.income || 0,
        expense: data.expense || 0
      })) 
    : [];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 w-48 bg-[#E7E5E4] rounded-lg animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-[#E7E5E4] rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="finances-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-medium text-[#292524] tracking-tight">Finances</h1>
          <p className="text-[#78716C] mt-1">Track your income and expenses</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Currency Switcher */}
          <div className="flex items-center gap-2 bg-[#F5F5F4] rounded-full p-1">
            <Button
              variant={currency === 'USD' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-full h-9 px-4 ${currency === 'USD' ? 'bg-[#57534E] text-white' : 'text-[#78716C]'}`}
              onClick={() => setCurrency('USD')}
              data-testid="currency-usd-btn"
            >
              $ USD
            </Button>
            <Button
              variant={currency === 'EUR' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-full h-9 px-4 ${currency === 'EUR' ? 'bg-[#57534E] text-white' : 'text-[#78716C]'}`}
              onClick={() => setCurrency('EUR')}
              data-testid="currency-eur-btn"
            >
              € EUR
            </Button>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="rounded-full bg-[#57534E] hover:bg-[#57534E]/90 h-11 px-6"
                onClick={() => openAddDialog()}
                data-testid="add-transaction-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Transaction
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-medium">New Transaction</DialogTitle>
                <DialogDescription className="sr-only">Transaction form</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={formData.type === 'income' ? 'default' : 'outline'}
                      className={`flex-1 rounded-full ${formData.type === 'income' ? 'bg-[#78866B] hover:bg-[#78866B]/90' : ''}`}
                      onClick={() => setFormData({ ...formData, type: 'income', category: '' })}
                      data-testid="type-income-btn"
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Income
                    </Button>
                    <Button
                      type="button"
                      variant={formData.type === 'expense' ? 'default' : 'outline'}
                      className={`flex-1 rounded-full ${formData.type === 'expense' ? 'bg-[#C98E75] hover:bg-[#C98E75]/90' : ''}`}
                      onClick={() => setFormData({ ...formData, type: 'expense', category: '' })}
                      data-testid="type-expense-btn"
                    >
                      <TrendingDown className="w-4 h-4 mr-2" />
                      Expense
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount">Amount ({getCurrencySymbol()}) *</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C]">{getCurrencySymbol()}</span>
                      <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        placeholder="0.00"
                        className="h-11 pl-8"
                        data-testid="transaction-amount-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date">Date</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="h-11"
                      data-testid="transaction-date-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="h-11" data-testid="transaction-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {(formData.type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client">Link to Client (optional)</Label>
                  <Select
                    value={formData.client_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, client_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger className="h-11" data-testid="transaction-client-select">
                      <SelectValue placeholder="Select a client" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional notes"
                    className="h-11"
                    data-testid="transaction-description-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="rounded-full bg-[#57534E] hover:bg-[#57534E]/90"
                    disabled={saving}
                    data-testid="save-transaction-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save Transaction
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <Card className="border-0 shadow-sm bg-[#78866B]/5">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#78716C] font-medium">Total Income</p>
                <p className="text-3xl font-medium text-[#78866B] mt-1">
                  {formatAmount(summary?.total_income || 0)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#78866B]/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-[#78866B]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-[#C98E75]/5">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#78716C] font-medium">Total Expenses</p>
                <p className="text-3xl font-medium text-[#C98E75] mt-1">
                  {formatAmount(summary?.total_expenses || 0)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#C98E75]/10 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-[#C98E75]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={`border-0 shadow-sm ${(summary?.net || 0) >= 0 ? 'bg-[#78866B]/5' : 'bg-red-50'}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#78716C] font-medium">Net Income</p>
                <p className={`text-3xl font-medium mt-1 ${(summary?.net || 0) >= 0 ? 'text-[#78866B]' : 'text-red-500'}`}>
                  {formatAmount(summary?.net || 0)}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${(summary?.net || 0) >= 0 ? 'bg-[#78866B]/10' : 'bg-red-100'}`}>
                <Coins className={`w-6 h-6 ${(summary?.net || 0) >= 0 ? 'text-[#78866B]' : 'text-red-500'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl font-medium">Monthly Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="month" stroke="#78716C" fontSize={12} />
                  <YAxis stroke="#78716C" fontSize={12} tickFormatter={(v) => `${getCurrencySymbol()}${v}`} />
                  <Tooltip 
                    formatter={(value) => [`${formatAmount(value)}`, '']}
                    contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="income" 
                    stackId="1"
                    stroke="#78866B" 
                    fill="#78866B" 
                    fillOpacity={0.2}
                    name="Income"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="expense" 
                    stackId="2"
                    stroke="#C98E75" 
                    fill="#C98E75" 
                    fillOpacity={0.2}
                    name="Expenses"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transactions List */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-medium">Transactions</CardTitle>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-[#F5F5F4]">
                <TabsTrigger value="all" data-testid="all-transactions-tab">All</TabsTrigger>
                <TabsTrigger value="income" data-testid="income-transactions-tab">Income</TabsTrigger>
                <TabsTrigger value="expense" data-testid="expense-transactions-tab">Expenses</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {filteredTransactions.length === 0 ? (
            <div className="py-12 text-center">
              <Coins className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
              <p className="text-[#78716C]">No transactions yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTransactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-4 rounded-xl hover:bg-[#F5F5F4] transition-colors group"
                  data-testid={`transaction-${transaction.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      transaction.type === 'income' ? 'bg-[#78866B]/10' : 'bg-[#C98E75]/10'
                    }`}>
                      {transaction.type === 'income' ? (
                        <TrendingUp className="w-5 h-5 text-[#78866B]" />
                      ) : (
                        <TrendingDown className="w-5 h-5 text-[#C98E75]" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[#292524]">{transaction.category}</span>
                        <Badge variant="outline" className="text-xs rounded-full">
                          {transaction.type}
                        </Badge>
                      </div>
                      <p className="text-sm text-[#78716C]">
                        {format(new Date(transaction.date), 'MMM d, yyyy')}
                        {transaction.description && ` • ${transaction.description}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`font-medium ${
                      transaction.type === 'income' ? 'text-[#78866B]' : 'text-[#C98E75]'
                    }`}>
                      {transaction.type === 'income' ? '+' : '-'}{formatAmount(transaction.amount)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(transaction)}
                      data-testid={`delete-transaction-${transaction.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
